/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { toRelativeUrl } from '@okta/okta-auth-js';

import { OKTA_ISSUER } from './api/api_config';
import { fetchOtkaClientId } from './api/pearlApi';

export interface AuthConfig {
  issuer: string;
  clientId?: string;
  redirectUri: string;
  scopes: string[];
  pkce: boolean;
  tokenManager?: {
    storageKey?: string;
  };
  storageManager?: {
    token?: {
      storageTypes?: Array<'localStorage' | 'sessionStorage' | 'cookie' | 'memory'>;
      storageKey?: string;
    };
    cache?: {
      storageTypes?: Array<'localStorage' | 'sessionStorage' | 'cookie' | 'memory'>;
      storageKey?: string;
    };
  };
}

export async function oktaAuthConfig(application: string) {
  const url = new URL(window.location.toString());
  const defaultClientKey = 'prov';
  let [, clientKey] = url.pathname.split('/');
  const redirectKey = sessionStorage.getItem('logoutRedirect.key') || defaultClientKey;
  if (!clientKey) clientKey = redirectKey;
  // for first time save url user tying to login before redirection to login page
  // clear this on logout
  if (!sessionStorage.getItem('redirectUri')) {
    const redirectUri = toRelativeUrl(window.location.href, window.location.origin);
    sessionStorage.setItem('redirectUri', redirectUri);
  }

  const keys = [clientKey, redirectKey, defaultClientKey];

  const OKTA_CLIENT_ID = await fetchOtkaClientId(application, keys);

  // store clientKey for logout redirect
  if (OKTA_CLIENT_ID) sessionStorage.setItem('logoutRedirect.key', keys[OKTA_CLIENT_ID?.usedKey] || '');

  const oktaAuthConfig: AuthConfig = {
    issuer: OKTA_ISSUER,
    clientId: OKTA_CLIENT_ID?.id,
    redirectUri: `${window.location.origin}/${keys[OKTA_CLIENT_ID?.usedKey!]}/login/callback`,
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    pkce: true,
    // Change the token and cache storage keys to be able to switch to Dynamic Token Issuer
    // in Okta configuration and avoid issuer conflicts in existing browser sessions
    tokenManager: {
      storageKey: 'okta-token-storage-v2', // replaces okta-token-storage
    },
    storageManager: {
      token: {
        storageTypes: ['localStorage'],
        storageKey: 'okta-token-storage-v2',
      },
      cache: {
        storageTypes: ['localStorage'],
        storageKey: 'okta-cache-storage-v2', // replaces okta-cache-storage
      },
    },
  };

  return oktaAuthConfig;
}
